import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, DollarSign, Car, User, Calendar } from 'lucide-react';
import { api } from '../../services/api';
import { PageHeader, DataTable, Column, SortConfig, SearchInput } from '../../components/ui';
import { StatCard, Badge, buttonClass, card } from '../../design-system';
import { formatCurrency, formatDate, formatPlaca } from '../../utils/format';
import { PAGE_SIZE } from '../../constants';

interface VendaList {
  id: number;
  clienteId: number;
  clienteNome: string;
  veiculoId: number;
  veiculoNome: string;
  veiculoPlaca: string | null;
  vendedorId: number;
  vendedorNome: string;
  valorFinal: number;
  formaPagamento: string;
  valorTroca: number | null;
  dataVenda: string;
}

interface PagedResult {
  items: VendaList[];
  totalItems: number;
  page: number;
  pageSize: number;
}

const formaPagamentoLabels: Record<string, string> = {
  AVISTA: 'À Vista',
  FINANCIAMENTO: 'Financiamento',
  CONSORCIO: 'Consórcio',
  CARTAO: 'Cartão',
  BOLETO: 'Boleto',
  PIX: 'PIX',
};

export function VendasList() {
  const navigate = useNavigate();
  const [data, setData] = useState<PagedResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [busca, setBusca] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'dataVenda', direction: 'desc' });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: PAGE_SIZE.toString(),
      });
      if (busca) params.append('busca', busca);
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (sortConfig.direction) {
        params.append('orderBy', sortConfig.key);
        params.append('orderDirection', sortConfig.direction);
      }

      const response = await api.get<PagedResult>(`/vendas?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Erro ao buscar vendas:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, busca, dataInicio, dataFim, sortConfig]);

  const handleSort = (config: SortConfig) => {
    setSortConfig(config);
    setPage(1);
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchData();
    return () => controller.abort();
  }, [fetchData]);

  const columns: Column<VendaList>[] = [
    {
      key: 'dataVenda',
      header: 'Data',
      width: '100px',
      sortable: true,
      render: (item) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <span>{formatDate(item.dataVenda)}</span>
        </div>
      ),
    },
    {
      key: 'veiculoNome',
      header: 'Veículo',
      sortable: true,
      render: (item) => (
        <div>
          <div className="font-medium text-slate-900">{item.veiculoNome}</div>
          {item.veiculoPlaca && (
            <div className="font-mono text-sm text-slate-500">{formatPlaca(item.veiculoPlaca)}</div>
          )}
        </div>
      ),
    },
    {
      key: 'clienteNome',
      header: 'Cliente',
      sortable: true,
      render: (item) => (
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-slate-400" />
          <span>{item.clienteNome}</span>
        </div>
      ),
    },
    {
      key: 'vendedorNome',
      header: 'Vendedor',
      sortable: true,
      render: (item) => item.vendedorNome,
    },
    {
      key: 'formaPagamento',
      header: 'Pagamento',
      sortable: true,
      render: (item) => (
        <Badge tone="neutral">
          {formaPagamentoLabels[item.formaPagamento] || item.formaPagamento}
        </Badge>
      ),
    },
    {
      key: 'valorFinal',
      header: 'Valor',
      align: 'right',
      sortable: true,
      render: (item) => (
        <div>
          <div className="font-semibold tabular-nums text-emerald-600">{formatCurrency(item.valorFinal)}</div>
          {item.valorTroca && (
            <div className="text-xs tabular-nums text-slate-500">Troca: {formatCurrency(item.valorTroca)}</div>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (item) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/vendas/${item.id}`);
          }}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-primary-600"
          title="Ver detalhes"
        >
          <Eye className="w-4 h-4" />
        </button>
      ),
    },
  ];

  // Calcular totais
  const totalVendas = data?.items.reduce((acc, v) => acc + v.valorFinal, 0) || 0;

  return (
    <div className="space-y-6">
      <PageHeader title="Vendas" subtitle="Histórico de vendas realizadas">
        <button onClick={() => navigate('/vendas/nova')} className={buttonClass('primary')}>
          <Plus className="w-4 h-4" />
          Nova Venda
        </button>
      </PageHeader>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          title="Total na Página"
          value={formatCurrency(totalVendas)}
          icon={<DollarSign className="h-[18px] w-[18px]" />}
          tone="positive"
          delay={0}
        />
        <StatCard
          title="Veículos Vendidos"
          value={data?.totalItems || 0}
          icon={<Car className="h-[18px] w-[18px]" />}
          delay={60}
        />
        <StatCard
          title="Ticket Médio"
          value={data?.items.length ? formatCurrency(totalVendas / data.items.length) : '-'}
          icon={<DollarSign className="h-[18px] w-[18px]" />}
          delay={120}
        />
      </div>

      {/* Filters */}
      <div className={`${card} p-4`}>
        <div className="flex flex-wrap gap-4">
          <SearchInput
            value={busca}
            onChange={setBusca}
            placeholder="Buscar por cliente, veículo ou placa..."
          />
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="h-10 rounded-xl border border-slate-200 px-3 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="Data início"
            />
            <span className="text-slate-400">até</span>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="h-10 rounded-xl border border-slate-200 px-3 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="Data fim"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.items || []}
        keyField="id"
        isLoading={isLoading}
        emptyMessage="Nenhuma venda encontrada"
        page={page}
        pageSize={PAGE_SIZE}
        totalItems={data?.totalItems}
        onPageChange={setPage}
        sortConfig={sortConfig}
        onSort={handleSort}
        onRowClick={(item) => navigate(`/vendas/${item.id}`)}
      />
    </div>
  );
}
